
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    MdArrowBack,
    MdSend,
    MdAdd,
    MdDelete,
    MdEdit,
    MdSave
} from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import PaginatedSelect from '../components/common/PaginatedSelect';
import './AddGenerateRequest.css';
import './AddProject.css'; // Reuse common form styles
import Swal from 'sweetalert2';
import axios from 'axios';
import {
    CREATE_PURCHASE_ORDER_API,
    UPDATE_PURCHASE_ORDER_API,
    VIEW_PURCHASE_ORDER_API,
    GET_PROJECT_VENDORS_API,
    GET_ALL_PROJECT_API,
    GET_ALL_VENDORS_API,
    GET_ALL_EMPLOYEES_API,
    GET_ALL_PRODUCTS_API,
    CREATE_VENDOR_API,
    CREATE_PROJECT_API,
    REGISTER_API,
    CREATE_PRODUCT_API,
    GET_ALL_CUSTOMERS_API,
    GET_ALL_ROLE_API,
    GET_ALL_PRODUCT_CATEGORIES_API,
    GET_ALL_UNITS_API,
    GET_ALL_PROJECT_CATEGORIES_API,
    GET_ALL_PROJECT_SUB_CATEGORIES_API,
    GET_ALL_PROJECT_SUB_CATEGORY_DETAILS_API,
    SEND_WHATSAPP_LINK_API,
    VENDOR_PORTAL_URL
} from '../config/API';
import { encryptToken } from '../utils/secureToken';

import Loader from '../components/common/Loader';
import Modal from '../components/common/Modal'; // Import the new Modal
import { showErrorToast } from '../utils/toastUtils';

const AddGenerateRequest = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [dropdownCacheUniq, setDropdownCacheUniq] = useState(Date.now());
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('Loading...');
    const [whatsappLoading, setWhatsappLoading] = useState(false);
    const [whatsappProgress, setWhatsappProgress] = useState({ current: 0, total: 0, vendorName: '' });
    const [fetchingData, setFetchingData] = useState(false);
    const [localId, setLocalId] = useState(null);

    // --- Quick Create State ---
    const [activeModal, setActiveModal] = useState(null); // 'vendor', 'project', 'employee', 'product'
    const [quickAddFormData, setQuickAddFormData] = useState({});
    const [modalLoading, setModalLoading] = useState(false);
    const [modalOptions, setModalOptions] = useState({ // Store options for modals (roles, categories, customers)
        customers: [],
        roles: [],
        categories: []
    });
    const [vendorOptions, setVendorOptions] = useState([]);

    // Helper to get initial employee from localStorage
    const getInitialEmployee = () => {
        try {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                const user = JSON.parse(storedUser);
                return {
                    value: user.id || user._id,
                    label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || 'Current User'
                };
            }
        } catch (e) {
            console.error('Error parsing user from localStorage:', e);
        }
        return null;
    };

    const [formData, setFormData] = useState({
        vendorId: [],
        projectId: null,
        employeeId: !id ? getInitialEmployee() : null,
        date: new Date(),
        validTill: null,
        particular: '',
        status: 'pending'
    });

    const [items, setItems] = useState([]);
    const [grandTotal, setGrandTotal] = useState(0);

    const refreshDropdowns = () => setDropdownCacheUniq(Date.now());

    useEffect(() => {
        const total = items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
        setGrandTotal(total);
    }, [items]);

    // Item data fetching is now handled by PaginatedSelect

    // Check for Edit Mode
    useEffect(() => {
        if (!id) return;

        const fetchPoDetails = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');
                const response = await axios.get(VIEW_PURCHASE_ORDER_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    const po = response.data.data;

                    // Pre-fill General Details
                    let mappedVendors = [];
                    const requestVendors = po.RequestVendors || po.PurchaseOrderVendors || [];
                    if (requestVendors.length > 0) {
                        const uniqueVendors = [...new Map(requestVendors.map(rv => [rv.Vendor?.id || rv.Vendor?._id, rv])).values()];
                        mappedVendors = uniqueVendors.map(rv => ({
                            value: rv.Vendor?.id || rv.Vendor?._id,
                            label: rv.Vendor?.companyName ? `${rv.Vendor?.vendorName} (${rv.Vendor?.companyName})` : (rv.Vendor?.vendorName || 'Unknown Vendor')
                        }));
                    } else if (po.vendorId) {
                        const vName = po.Vendor?.vendorName || po.vendorName || 'Selected Vendor';
                        const cName = po.Vendor?.companyName || po.companyName;
                        mappedVendors = [{
                            value: po.vendorId,
                            label: cName ? `${vName} (${cName})` : vName
                        }];
                    }

                    setFormData({
                        vendorId: mappedVendors,
                        projectId: { value: po.projectId, label: po.ProjectMaster?.projectName || po.Project?.projectName || 'Selected Project' },
                        employeeId: { value: po.employeeId, label: `${po.Employee?.firstName || ''} ${po.Employee?.lastName || ''}`.trim() || po.Employee?.fullName || 'Selected Employee' },
                        date: new Date(po.date),
                        validTill: (po.tillDate || po.validTill) ? new Date(po.tillDate || po.validTill) : null,
                        particular: po.particular || '',
                        status: po.status || 'pending'
                    });

                    // Pre-fill Items
                    const requestItems = po.RequestItems || po.PurchaseOrderItems || [];
                    if (Array.isArray(requestItems)) {
                        const seenItems = new Set();
                        const uniqueItems = [];
                        requestItems.forEach(item => {
                            const prodId = item.productId || item.Product?.id || item.Product?._id;
                            const key = `${prodId}-${item.productDescription}-${item.size}-${item.qty}-${item.rate}`;
                            if (!seenItems.has(key)) {
                                seenItems.add(key);
                                uniqueItems.push(item);
                            }
                        });

                        const loadedItems = uniqueItems.map((item, index) => ({
                            id: Date.now() + index, // Unique ID for key
                            srNo: index + 1,
                            reqItemId: item.id || item._id,
                            productId: item.productId || item.Product?.id || item.Product?._id,
                            productName: item.Product?.productName || item.productDescription,
                            productDescription: item.productDescription,
                            unitId: item.unitId || item.Unit?.id || item.Product?.Unit?.id || item.Product?.Unit?._id || '',
                            unitName: item.unit || item.Unit?.unitName || item.Product?.Unit?.unitName || '',
                            size: item.size || '',
                            qty: item.qty,
                            rate: item.rate,
                            total: item.total
                        }));
                        setItems(loadedItems);
                    }
                } else {
                    console.error('Failed to fetch Request details:', response.data.message);
                }
            } catch (error) {
                console.error('Error fetching Request details:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPoDetails();
    }, [id]);


    const fetchProjectVendors = async () => {
        if (!formData.projectId?.value) {
            setVendorOptions([]);
            // Clear selected vendors when no project is selected
            setFormData(prev => ({ ...prev, vendorId: [] }));
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_PROJECT_VENDORS_API(formData.projectId.value), {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                const { assignedVendors = [], unassignedVendors = [] } = response.data.data || {};

                const mapVendor = (v) => ({
                    value: v.id || v._id,
                    label: v.companyName ? `${v.vendorName} (${v.companyName})` : v.vendorName,
                    whatsappNumber: v.whatsAppNumber || v.whatsappNumber || v.whatsapp_number || v.contactNumber || v.mobileNumber || ''
                });

                const groupedOptions = [];
                if (assignedVendors.length > 0) {
                    groupedOptions.push({
                        label: 'Assigned Vendors',
                        options: assignedVendors.map(mapVendor)
                    });
                }
                if (unassignedVendors.length > 0) {
                    groupedOptions.push({
                        label: 'All Other Vendors',
                        options: unassignedVendors.map(mapVendor)
                    });
                }

                // Set the vendor options
                setVendorOptions(groupedOptions.length > 0 ? groupedOptions : []);

                // Clear previously selected vendors when project changes
                setFormData(prev => ({ ...prev, vendorId: [] }));
            }
        } catch (error) {
            console.error('Error fetching project vendors:', error);
            setVendorOptions([]);
        }
    };

    useEffect(() => {
        fetchProjectVendors();
    }, [formData.projectId?.value]); // This will trigger when project ID changes



    const handleSelectChange = (selected, field) => {
        setFormData(prev => ({
            ...prev,
            [field]: selected
        }));

        // If project changes, clear vendors
        if (field === 'projectId') {
            setFormData(prev => ({
                ...prev,
                vendorId: [] // Clear vendors when project changes
            }));
        }
    };
    const handleDateChange = (date) => {
        setFormData(prev => ({
            ...prev,
            date: date
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const removeItemRow = (id) => {
        if (items.length === 0) return;
        const filtered = items.filter(item => item.id !== id);
        // Re-calculate SR Nos
        const reOrdered = filtered.map((item, index) => ({
            ...item,
            srNo: index + 1
        }));
        setItems(reOrdered);
    };

    // --- Item Modal State ---
    const [itemModalOpen, setItemModalOpen] = useState(false);
    const [itemModalData, setItemModalData] = useState({
        productId: '',
        productName: '',
        productDescription: '',
        unit: null,
        size: '',
        qty: 1,
        rate: 0,
        total: 0
    });
    const [isEditingItem, setIsEditingItem] = useState(false);

    const openItemModal = (itemData, isEdit = false) => {
        setIsEditingItem(isEdit);
        setItemModalData({
            ...itemData,
            unit: itemData.unitId ? { value: itemData.unitId, label: itemData.unitName || itemData.unit } :
                (itemData.unit ? { value: itemData.unit, label: itemData.unit } : null)
        });
        setItemModalOpen(true);
    };

    const handleItemModalChange = (field, value) => {
        setItemModalData(prev => {
            const updates = { [field]: value };
            if (field === 'qty' || field === 'rate') {
                const q = field === 'qty' ? parseFloat(value) || 0 : parseFloat(prev.qty) || 0;
                const r = field === 'rate' ? parseFloat(value) || 0 : parseFloat(prev.rate) || 0;
                updates.total = (q * r).toFixed(2);
            }
            return { ...prev, ...updates };
        });
    };

    const handleSaveItem = () => {

        if (itemModalData.qty <= 0) {
            Swal.fire('Error', 'Quantity must be greater than 0', 'error');
            return;
        }

        const newItem = {
            ...itemModalData,
            unitId: itemModalData.unit ? itemModalData.unit.value : '',
            unitName: itemModalData.unit ? itemModalData.unit.label : '',
            qty: parseFloat(itemModalData.qty),
            rate: parseFloat(itemModalData.rate),
            total: parseFloat(itemModalData.total)
        };

        if (isEditingItem) {
            setItems(prev => prev.map(it => it.id === newItem.id ? newItem : it));
        } else {
            setItems(prev => [...prev, { ...newItem, id: Date.now(), srNo: items.length + 1 }]);
        }
        setItemModalOpen(false);
    };

    const handleItemSelect = (selected) => {
        if (!selected) return;

        openItemModal({
            productId: selected.value,
            productName: selected.label,
            productDescription: selected.description,
            unitId: selected.unitId || '',
            unitName: selected.unitName || '',
            size: selected.size || '',
            qty: 1,
            rate: selected.rate || 0,
            total: selected.rate || 0
        });
    };

    const handleEditItem = (item) => {
        openItemModal(item, true);
    };                // --- Quick Create Handlers ---

    const handleAddVendor = () => {
        setQuickAddFormData({});
        setActiveModal('vendor');
    };

    const handleAddProject = async () => {
        try {
            setModalLoading(true);
            const token = localStorage.getItem('accessToken');
            const [custRes, catRes, subCatRes, detRes] = await Promise.all([
                axios.get(GET_ALL_CUSTOMERS_API, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(GET_ALL_PROJECT_CATEGORIES_API, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(GET_ALL_PROJECT_SUB_CATEGORIES_API, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(GET_ALL_PROJECT_SUB_CATEGORY_DETAILS_API, { headers: { Authorization: `Bearer ${token}` } })
            ]);

            let options = {};
            if (custRes.data.status) {
                options.customers = custRes.data.data.map(c => ({ value: c.id, label: c.customerName }));
            }
            if (catRes.data.status) {
                options.projectCategories = catRes.data.data.map(c => ({ value: c.id || c._id, label: c.categoryName }));
            }
            if (subCatRes.data.status) {
                options.projectSubCategories = subCatRes.data.data.map(c => ({ value: c.id || c._id, label: c.categoryName }));
            }
            if (detRes.data.status) {
                options.projectDetails = detRes.data.data.map(c => ({ value: c.id || c._id, label: c.detailName }));
            }
            setModalOptions(prev => ({ ...prev, ...options }));

        } catch (e) {
            console.error(e);
            showErrorToast('Failed to load project options');
        } finally {
            setModalLoading(false);
        }
        setQuickAddFormData({});
        setActiveModal('project');
    };

    const handleAddEmployee = async () => {
        try {
            setModalLoading(true);
            const res = await fetch(GET_ALL_ROLE_API);
            const result = await res.json();
            if (result.status) {
                const roles = result.data
                    .filter(r => r.roleName !== 'SUPER_ADMIN')
                    .map(r => ({ value: r.id, label: r.roleName }));
                setModalOptions(prev => ({ ...prev, roles }));
            }
        } catch (e) {
            showErrorToast('Failed to load roles');
        } finally {
            setModalLoading(false);
        }
        setQuickAddFormData({});
        setActiveModal('employee');
    };

    const handleAddProduct = async () => {
        try {
            setModalLoading(true);
            const token = localStorage.getItem('accessToken');
            const res = await axios.get(GET_ALL_PRODUCT_CATEGORIES_API, { headers: { Authorization: `Bearer ${token}` } });
            if (res.data.status) {
                const categories = res.data.data.map(c => ({
                    value: c.id || c._id,
                    label: c.categoryName,
                    unitId: c.unitId || c.Unit?._id || c.Unit?.id || '',
                    unitName: c.Unit?.unitName || c.unitName || ''
                }));
                setModalOptions(prev => ({ ...prev, categories }));
            }
        } catch (e) {
            showErrorToast('Failed to load categories');
        } finally {
            setModalLoading(false);
        }
        setQuickAddFormData({});
        setActiveModal('product');
    };

    const handleQuickAddSubmit = async () => {
        const token = localStorage.getItem('accessToken');
        let apiEndpoint = '';
        let payload = { ...quickAddFormData };
        let successMessage = '';
        let isValid = true;

        if (activeModal === 'vendor') {
            if (!payload.vendorName) { showErrorToast('Vendor Name is required'); isValid = false; }
            apiEndpoint = CREATE_VENDOR_API;
            successMessage = 'Vendor created successfully';
        } else if (activeModal === 'project') {
            if (!payload.projectName) { showErrorToast('Project Name is required'); isValid = false; }
            if (payload.customerId && payload.customerId.value) {
                payload.customerId = payload.customerId.value;
            }
            if (payload.projectCategoryId && payload.projectCategoryId.value) {
                payload.projectCategoryId = payload.projectCategoryId.value;
            }
            if (payload.projectSubCategoryId && payload.projectSubCategoryId.value) {
                payload.projectSubCategoryId = payload.projectSubCategoryId.value;
            }
            if (payload.projectSubCategoryDetailId && payload.projectSubCategoryDetailId.value) {
                payload.projectSubCategoryDetailId = payload.projectSubCategoryDetailId.value;
            }
            if (payload.vendorIds && Array.isArray(payload.vendorIds)) {
                payload.vendorIds = payload.vendorIds.map(v => v.value);
            }
            payload.status = 'active';
            apiEndpoint = CREATE_PROJECT_API;
            successMessage = 'Project created successfully';
        } else if (activeModal === 'employee') {
            if (payload.password !== payload.confirmPassword) {
                showErrorToast('Passwords do not match'); isValid = false;
            }
            if (isValid) {
                if (!payload.userName) payload.userName = payload.email;
                // confirmPassword is required by the backend
                apiEndpoint = REGISTER_API;
                successMessage = 'Employee created successfully';
            }
        } else if (activeModal === 'product') {

            if (payload.productCategoryId && payload.productCategoryId.value) {
                payload.productCategoryId = payload.productCategoryId.value;
            }
            if (payload.unitId && payload.unitId.value) {
                payload.unitId = payload.unitId.value;
            }
            apiEndpoint = CREATE_PRODUCT_API;
            successMessage = 'Product created successfully';
        }

        if (!isValid) return;

        try {
            setModalLoading(true);
            let response;
            if (activeModal === 'employee') {
                const res = await fetch(apiEndpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify(payload)
                });
                const data = await res.json();
                response = { data: data, status: res.status, ok: res.ok };
            } else {
                response = await axios.post(apiEndpoint, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            const result = response.data;
            console.log('Quick Add Result:', result);
            const isSuccess = response.ok || result.status;

            if (isSuccess) {
                if (activeModal === 'vendor' && result.data) {
                    const newVendor = result.data;
                    const vName = newVendor.vendorName || newVendor.name || 'New Vendor';
                    const cName = newVendor.companyName;
                    const label = cName ? `${vName} (${cName})` : vName;
                    const newOption = {
                        value: newVendor.id || newVendor._id,
                        label: label,
                        whatsappNumber: newVendor.whatsAppNumber || newVendor.whatsappNumber || newVendor.whatsapp_number || newVendor.contactNumber || newVendor.mobileNumber || ''
                    };
                    refreshDropdowns();
                    setFormData(prev => ({ ...prev, vendorId: [...prev.vendorId, newOption] }));
                } else if (activeModal === 'project' && result.data) {
                    const newProject = result.data;
                    const newOption = { value: newProject.id || newProject._id, label: newProject.projectName };
                    refreshDropdowns();
                    setFormData(prev => ({ ...prev, projectId: newOption }));
                } else if (activeModal === 'employee') {
                    const newEmp = result.data || result.user || result.employee;
                    if (newEmp) {
                        const newOption = { value: newEmp.id || newEmp._id, label: `${newEmp.firstName || ''} ${newEmp.lastName || ''}`.trim() || quickAddFormData.firstName };
                        refreshDropdowns();
                        setFormData(prev => ({ ...prev, employeeId: newOption }));
                    }
                } else if (activeModal === 'product') {
                    const newProd = result.data?.data || result.data || result;
                    if (newProd && (newProd.id || newProd._id)) {
                        const originalUnitOption = quickAddFormData.unitId;
                        const newOption = {
                            value: newProd.id || newProd._id,
                            label: newProd.productName,
                            unitId: newProd.unitId || (newProd.Unit?.id || newProd.Unit?._id) || (originalUnitOption?.value) || '',
                            unitName: newProd.unitName || newProd.Unit?.unitName || newProd.unit || (originalUnitOption?.label) || '',
                            rate: newProd.defaultRate
                        };
                        refreshDropdowns();
                    }
                }
                Swal.fire('Success', successMessage, 'success');
                setActiveModal(null);
            } else {
                showErrorToast(result.message || 'Failed to create');
            }
        } catch (error) {
            console.error('Quick Add Error:', error);
            showErrorToast('An error occurred during creation');
        } finally {
            setModalLoading(false);
        }
    };

    const handleQuickAddChange = (e) => {
        const { name, value } = e.target;
        setQuickAddFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleQuickAddSelectChange = (selected, field) => {
        setQuickAddFormData(prev => {
            const updates = { [field]: selected };

            // Auto-fill unit when category is selected in product modal
            if (activeModal === 'product' && field === 'productCategoryId' && selected) {
                updates.unitId = selected.unitId || '';
                updates.unitName = selected.unitName || '';
            }

            return { ...prev, ...updates };
        });
    };

    const handleAction = async (isDraft = false) => {

        if (loading) return;

        const result = await Swal.fire({
            title: isDraft ? (id ? 'Save as Draft?' : 'Save as Draft?') : (id ? 'Update Generate Request?' : 'Finalize Generate Request?'),
            text: isDraft ? 'Are you sure you want to save this as a draft?' : (id ? 'Are you sure you want to update this Generate Request?' : 'Are you sure you want to create this Generate Request?'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: isDraft ? 'Yes, Save Draft' : 'Yes, Finalize',
            cancelButtonText: 'Cancel',
            confirmButtonColor: isDraft ? '#64748b' : '#10b981',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                setLoadingText(isDraft ? 'Saving Draft...' : (id ? 'Updating Request...' : 'Creating Request...'));
                const token = localStorage.getItem('accessToken');
                const vendorIds = formData.vendorId ? formData.vendorId.map(v => v.value) : [];
                const payload = {
                    projectId: formData.projectId?.value,
                    employeeId: formData.employeeId?.value,
                    vendors: vendorIds,
                    date: formData.date?.toISOString().split('T')[0],
                    tillDate: formData.validTill?.toISOString().split('T')[0],
                    particular: formData.particular,
                    items: items.map(({ productId, productDescription, size, qty, rate, unitId, reqItemId }) => {
                        const itemObj = {
                            productId,
                            productDescription: productDescription || "N/A",
                            size,
                            qty,
                            rate,
                            unitId
                        };
                        if (reqItemId) {
                            itemObj.reqItemId = parseInt(reqItemId, 10);
                        }
                        return itemObj;
                    })
                };
                const effectiveId = id || localId;
                const response = effectiveId
                    ? await axios.put(UPDATE_PURCHASE_ORDER_API(effectiveId), payload, { headers: { Authorization: `Bearer ${token}` } })
                    : await axios.post(CREATE_PURCHASE_ORDER_API, payload, { headers: { Authorization: `Bearer ${token}` } });

                if (response.data.status) {
                    setLoading(false); // Stop global loader to show WhatsApp overlay

                    // --- Automated WhatsApp Notification ---
                    let finalRequestId = id;
                    if (!finalRequestId) {
                        const resData = response.data.data;
                        if (resData) {
                            if (typeof resData === 'number' || typeof resData === 'string') {
                                finalRequestId = resData;
                            } else if (Array.isArray(resData)) {
                                finalRequestId = resData[0]?.id || resData[0]?._id || resData[0];
                            } else if (typeof resData === 'object') {
                                finalRequestId = resData.id || resData._id || resData.reqId || resData.requestId;
                            }
                        }
                        if (!finalRequestId) {
                            finalRequestId = response.data.id || response.data._id || response.data.reqId;
                        }
                    }

                    // --- Automated WhatsApp Notification ---
                    // Skip WhatsApp if it's a draft
                    if (!isDraft) {
                        try {
                            console.log('Extracted Final Request ID:', finalRequestId);

                            if (finalRequestId) {
                                // Start Custom WhatsApp Loader
                                setWhatsappLoading(true);
                                const totalVendors = (formData.vendorId || []).length;
                                setWhatsappProgress({ current: 0, total: totalVendors, vendorName: '' });
                                const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

                                // 2. Loop through selected vendors and send links
                                for (let i = 0; i < totalVendors; i++) {
                                    const vendorSummary = formData.vendorId[i];

                                    setWhatsappProgress({
                                        current: i + 1,
                                        total: totalVendors,
                                        vendorName: vendorSummary.label
                                    });

                                    // --- Control Delay between consecutive calls ---
                                    if (i > 0) await sleep(2000); // 2 second delay between calls

                                    // Ensure we have the phone number - now directly in the option object
                                    const phone = vendorSummary.whatsappNumber ||
                                        vendorSummary.whatsAppNumber ||
                                        vendorSummary.whatsapp_number ||
                                        vendorSummary.contactNumber ||
                                        vendorSummary.mobileNumber || '';

                                    if (phone) {
                                        const encryptedRequestId = encryptToken(Number(finalRequestId));
                                        const encryptedVendorId = encryptToken(Number(vendorSummary.value));
                                        const portalLink = `${window.location.origin}/vendor-portal/${encryptedRequestId}/${encryptedVendorId}`;

                                        const whatsappPayload = {
                                            vendorId: vendorSummary.value,
                                            message: `Please click on this link to update rate for the generate request.\n\n${portalLink}`,
                                            link: portalLink
                                        };

                                        try {
                                            const response = await axios.post(SEND_WHATSAPP_LINK_API(finalRequestId), whatsappPayload, {
                                                headers: { Authorization: `Bearer ${token}` }
                                            });

                                            // ✅ Check for internal failures in response data even if status is 200/true
                                            const resBody = response.data;
                                            const resDataArr = resBody?.data;
                                            const internalError = Array.isArray(resDataArr) && resDataArr.length > 0 && resDataArr[0].error;

                                            if (internalError) {
                                                console.error(`Internal WhatsApp failure for ${vendorSummary.label}:`, internalError);
                                            }
                                        } catch (waInnerError) {
                                            console.error(`Network error sending WhatsApp to ${vendorSummary.label}:`, waInnerError);
                                        }
                                    }
                                }

                                // ✅ TRIGGER ADMIN NOTIFICATIONS: Exactly once after all vendors are processed.
                                if (formData.vendorId && formData.vendorId.length > 0) {
                                    try {
                                        await axios.post(
                                            SEND_WHATSAPP_LINK_API(finalRequestId),
                                            { message: "ignore_this_message" },
                                            { headers: { Authorization: `Bearer ${token}` } }
                                        );
                                    } catch (waAdminError) {
                                        console.error('Failed to send admin notification:', waAdminError);
                                    }
                                }

                                setWhatsappLoading(false);
                            }
                        } catch (waError) {
                            console.error('Error sending automated WhatsApp:', waError);
                            setWhatsappLoading(false);
                        }
                    }

                    await Swal.fire({
                        icon: 'success',
                        title: isDraft ? 'Draft Saved!' : (id ? 'Generate Request Updated!' : 'Generate Request Created!'),
                        text: isDraft ? 'The Generate Request has been saved as a draft.' : 'The Generate Request has been processed successfully.',
                        timer: 1500,
                        showConfirmButton: false,
                    });

                    if (!isDraft) {
                        navigate('/generate-requests');
                    } else if (!effectiveId && finalRequestId) {
                        setLocalId(finalRequestId);
                    }
                } else {
                    showErrorToast(response.data.error || response.data.message || 'Failed to process purchase order');
                }
            } catch (error) {
                console.error('Error with PO action:', error);
                const errorData = error.response?.data?.error || error.response?.data?.message || 'An error occurred';
                showErrorToast(errorData);
            } finally {
                setLoading(false);
            }
        }
    };



    if (loading && loadingText !== 'Saving Draft...') {
        return <Loader fullPage text={loadingText} />;
    }

    return (
        <div className="add-po-page">
            <div className="page-header">
                <button onClick={() => navigate('/generate-requests')} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>{id ? 'Update Generate Request' : 'Create Generate Request'}</h2>
            </div>

            <form className="project-form" onSubmit={(e) => e.preventDefault()}>
                <div className="form-section">
                    <h3>General Details</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Project Name / Site Name <span className="required-star">*</span></label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                    <PaginatedSelect
                                        apiUrl={GET_ALL_PROJECT_API}
                                        mapResponse={(p) => ({
                                            value: p.id || p._id,
                                            label: p.projectName
                                        })}
                                        value={formData.id}
                                        onChange={(sel) => {
                                            handleSelectChange(sel, 'projectId');
                                            handleSelectChange([], 'vendorId'); // Clear vendors when project changes
                                        }}
                                        placeholder="Select project"
                                        isClearable
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        cacheUniq={dropdownCacheUniq}
                                    />
                                </div>
                                <button type="button" onClick={handleAddProject} className="quick-add-btn" title="Add Project">
                                    <MdAdd size={20} />
                                </button>
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Vendor Name <span className="required-star">*</span></label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                    <Select
                                        options={vendorOptions}
                                        isMulti
                                        value={formData.vendorId}
                                        onChange={(sel) => handleSelectChange(sel, 'vendorId')}
                                        placeholder={formData.projectId?.value ? "Select vendor(s)" : "First select a project"}
                                        isClearable
                                        isDisabled={!formData.projectId?.value}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        key={`vendors-${formData.projectId?.value || 'no-project'}`}
                                    />
                                </div>
                                <button type="button" onClick={handleAddVendor} className="quick-add-btn" title="Add Vendor">
                                    <MdAdd size={20} />
                                </button>
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Order By (Employee) <span className="required-star">*</span></label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                    <PaginatedSelect
                                        apiUrl={GET_ALL_EMPLOYEES_API}
                                        mapResponse={(e) => ({
                                            value: e.id || e._id,
                                            label: `${e.firstName} ${e.lastName}`
                                        })}
                                        value={formData.employeeId}
                                        onChange={(sel) => handleSelectChange(sel, 'employeeId')}
                                        placeholder="Select employee"
                                        isClearable
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        cacheUniq={dropdownCacheUniq}
                                    />
                                </div>
                                <button type="button" onClick={handleAddEmployee} className="quick-add-btn" title="Add Employee">
                                    <MdAdd size={20} />
                                </button>
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Date <span className="required-star">*</span></label>
                            <DatePicker
                                selected={formData.date}
                                onChange={handleDateChange}
                                dateFormat="yyyy-MM-dd"
                                className="form-control"
                                placeholderText="Select date"
                            />
                        </div>
                        <div className="form-group">
                            <label>Date Till</label>
                            <DatePicker
                                selected={formData.validTill}
                                onChange={(date) => setFormData(prev => ({ ...prev, validTill: date }))}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                placeholderText="Select validity date"
                            />
                        </div>
                        <div className="form-group full-width">
                            <label>Purpose / Particular</label>
                            <textarea
                                rows={3}
                                name="particular"
                                value={formData.particular}
                                onChange={handleInputChange}
                                placeholder="Enter purpose of order"
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h3 style={{ margin: 0 }}>Items List</h3>
                    </div>

                    <div className="item-selection-wrapper">
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <PaginatedSelect
                                    apiUrl={GET_ALL_PRODUCTS_API}
                                    mapResponse={(p) => ({
                                        value: p.id || p._id,
                                        label: p.productName,
                                        description: p.description,
                                        size: p.size,
                                        unitId: p.Unit?.id || p.Unit?._id || p.unitId || '',
                                        unitName: p.Unit?.unitName || p.unitName || p.unit || '',
                                        rate: p.defaultRate || p.rate
                                    })}
                                    onChange={handleItemSelect}
                                    placeholder="Search & Select Product to Add"
                                    value={null}
                                    isClearable
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    cacheUniq={dropdownCacheUniq}
                                />
                            </div>
                            <button type="button" onClick={handleAddProduct} className="quick-add-btn" title="Add Product">
                                <MdAdd size={20} />
                            </button>
                        </div>
                    </div>

                    <div className="items-table-container">
                        <table className="items-table">
                            <thead className="thead-po">
                                <tr>
                                    <th style={{ width: '50px' }}>SR No</th>
                                    <th>Product Name</th>
                                    <th style={{ width: '250px' }}>Product Description</th>
                                    <th style={{ width: '100px' }}>Unit</th>
                                    <th style={{ width: '100px' }}>Size</th>
                                    <th style={{ width: '100px' }}>Qty</th>
                                    <th style={{ width: '120px' }}>Rate</th>
                                    <th style={{ width: '120px' }}>Total</th>
                                    <th style={{ width: '50px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => (
                                    <tr key={item.id}>
                                        <td style={{ textAlign: 'center' }}>{item.srNo}</td>
                                        <td>
                                            <div style={{ fontWeight: '500', color: '#1e293b' }}>
                                                {item.productName}
                                            </div>
                                        </td>
                                        <td style={{ width: '250px', minWidth: '250px', maxWidth: '250px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                                {item.productDescription}
                                            </div>
                                        </td>
                                        <td>{item.unitName || '-'}</td>
                                        <td>{item.size}</td>
                                        <td>{item.qty}</td>
                                        <td>{item.rate.toLocaleString()}</td>
                                        <td style={{ fontWeight: '600', color: '#4f46e5' }}>
                                            {item.total.toLocaleString()}
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    type="button"
                                                    className="item-edit-btn"
                                                    onClick={() => handleEditItem(item)}
                                                    style={{ border: 'none', background: '#f1f5f9', color: '#64748b', padding: '6px', borderRadius: '4px', cursor: 'pointer' }}
                                                >
                                                    <MdEdit size={18} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="item-delete-btn"
                                                    onClick={() => removeItemRow(item.id)}
                                                    style={{ border: 'none', background: '#fee2e2', color: '#ef4444', padding: '6px', borderRadius: '4px', cursor: 'pointer' }}
                                                >
                                                    <MdDelete size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {items.length === 0 && (
                                    <tr>
                                        <td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                                            No items added yet. Search and select a product above.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="mobile-items-view">
                        {items.length === 0 ? (
                            <div className="no-data">No items added yet.</div>
                        ) : (
                            items.map((item, index) => (
                                <div key={item.id} className="mobile-item-card">
                                    <div className="mobile-item-header">
                                        <div className="mobile-item-title">
                                            <div className="sr-badge">0{index + 1}</div>
                                            <strong>{item.productName}</strong>
                                        </div>
                                    </div>
                                    <div className="mobile-item-body">
                                        <div className="mobile-item-row full-width">
                                            <span className="m-label">Description</span>
                                            <span className="m-value">{item.productDescription || '-'}</span>
                                        </div>
                                        <div className="mobile-item-row">
                                            <span className="m-label">Qty</span>
                                            <span className="m-value">{item.qty} {item.unitName || 'Nos'}</span>
                                        </div>
                                        <div className="mobile-item-row">
                                            <span className="m-label">Size</span>
                                            <span className="m-value">{item.size || '-'}</span>
                                        </div>
                                        <div className="mobile-item-row">
                                            <span className="m-label">Rate</span>
                                            <span className="m-value">₹{item.rate.toLocaleString()}</span>
                                        </div>
                                        <div className="mobile-item-row">
                                            <span className="m-label">Total</span>
                                            <span className="m-value" style={{ color: '#4f46e5' }}>₹{item.total.toLocaleString()}</span>
                                        </div>
                                    </div>
                                    <div className="mobile-item-actions">
                                        <button type="button" onClick={() => handleEditItem(item)} className="item-edit-btn" style={{ border: 'none', background: '#f1f5f9', color: '#64748b', padding: '6px', borderRadius: '4px', cursor: 'pointer' }}>
                                            <MdEdit size={18} />
                                        </button>
                                        <button type="button" onClick={() => removeItemRow(item.id)} className="item-delete-btn" style={{ border: 'none', background: '#fee2e2', color: '#ef4444', padding: '6px', borderRadius: '4px', cursor: 'pointer' }}>
                                            <MdDelete size={18} />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <div className="po-summary">
                        <div className="grand-total-card">
                            <div className="total-label">Grand Total</div>
                            <div className="total-amount">{grandTotal.toLocaleString()}</div>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate('/generate-requests')} className="cancel-btn">
                        Cancel
                    </button>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <button
                            type="button"
                            className="save-btn draft-btn"
                            onClick={() => handleAction(true)}
                            disabled={loading}
                        >
                            <MdSave /> {loading && loadingText === 'Saving Draft...' ? 'Saving...' : 'Save as Draft'}
                        </button>
                        <button
                            type="button"
                            className="save-btn finalize-btn"
                            onClick={() => handleAction(false)}
                            disabled={loading}
                        >
                            <MdSend /> {id ? 'Update Request' : 'Create Request'}
                        </button>
                    </div>
                </div>
            </form>
            {/* Generic Quick Add Modal */}
            <Modal
                isOpen={!!activeModal}
                onClose={() => setActiveModal(null)}
                title={
                    activeModal === 'vendor' ? 'Add New Vendor' :
                        activeModal === 'project' ? 'Add New Project' :
                            activeModal === 'employee' ? 'Add New Employee' :
                                activeModal === 'product' ? 'Add New Product' : ''
                }
                footer={
                    <>
                        <button className="modal-btn-cancel" onClick={() => setActiveModal(null)} disabled={modalLoading}>Cancel</button>
                        <button className="modal-btn-primary" onClick={handleQuickAddSubmit} disabled={modalLoading}>
                            {modalLoading ? 'Creating...' : 'Create'}
                        </button>
                    </>
                }
            >
                {activeModal === 'vendor' && (
                    <div className="modal-grid">
                        <div className="modal-full-width modal-form-group">
                            <label>Company Name<span className="required-star">*</span></label>
                            <input name="companyName" placeholder="Enter company name" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-full-width modal-form-group">
                            <label>Vendor Name <span className="required-star">*</span></label>
                            <input name="vendorName" placeholder="Enter vendor name" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-form-group">
                            <label>Contact Person <span className="required-star">*</span></label>
                            <input name="contactPerson" placeholder="Contact person" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-form-group">
                            <label>WhatsApp Number <span className="required-star">*</span></label>
                            <input name="whatsappNumber" placeholder="Mobile / WhatsApp" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-full-width modal-form-group">
                            <label>Address</label>
                            <textarea name="address" rows="2" placeholder="Full address" onChange={handleQuickAddChange}></textarea>
                        </div>
                        <div className="modal-form-group">
                            <label>Category</label>
                            <input name="category" placeholder="e.g. Electronics" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-form-group">
                            <label>GST Number</label>
                            <input name="gst" placeholder="GST Number" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-form-group">
                            <label>PAN Number</label>
                            <input name="pan" placeholder="PAN Number" onChange={handleQuickAddChange} />
                        </div>
                    </div>
                )}

                {activeModal === 'project' && (
                    <div className="modal-grid">
                        <div className="modal-full-width modal-form-group">
                            <label>Select Customer <span className="required-star">*</span></label>
                            <Select
                                options={modalOptions.customers}
                                onChange={(sel) => handleQuickAddSelectChange(sel, 'customerId')}
                                placeholder="Select Customer"
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isClearable
                            />
                        </div>
                        <div className="modal-full-width modal-form-group">
                            <label>Select Vendor(s)</label>
                            <PaginatedSelect
                                apiUrl={GET_ALL_VENDORS_API}
                                mapResponse={(v) => ({ value: v.id || v._id, label: v.vendorName })}
                                isMulti
                                value={quickAddFormData.vendorIds}
                                onChange={(sel) => handleQuickAddSelectChange(sel, 'vendorIds')}
                                placeholder="Select Vendor(s)"
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isClearable
                            />
                        </div>
                        <div className="modal-full-width modal-form-group">
                            <label>Project Name <span className="required-star">*</span></label>
                            <input name="projectName" placeholder="Enter project name" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-form-group">
                            <label>Project Type Category</label>
                            <Select
                                options={modalOptions.projectCategories}
                                onChange={(sel) => handleQuickAddSelectChange(sel, 'projectCategoryId')}
                                placeholder="Select Category"
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isClearable
                            />
                        </div>
                        <div className="modal-form-group">
                            <label>Project Type Sub-Category</label>
                            <Select
                                options={modalOptions.projectSubCategories}
                                onChange={(sel) => handleQuickAddSelectChange(sel, 'projectSubCategoryId')}
                                placeholder="Select Sub-Category"
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isClearable
                            />
                        </div>
                        {quickAddFormData.projectSubCategoryId?.label?.toLowerCase() === 'apartment' && (
                            <div className="modal-form-group">
                                <label>BHK (Details)</label>
                                <Select
                                    options={modalOptions.projectDetails}
                                    onChange={(sel) => handleQuickAddSelectChange(sel, 'projectSubCategoryDetailId')}
                                    placeholder="Select BHK"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isClearable
                                />
                            </div>
                        )}
                        <div className="modal-full-width modal-form-group">
                            <label>Address / Location <span className="required-star">*</span></label>
                            <input name="projectAdd" placeholder="Project location" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-full-width modal-form-group">
                            <label>Google Map Link</label>
                            <input name="googleMapLink" placeholder="Google Map Link" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-form-group">
                            <label>Start Date</label>
                            <input type="date" name="startDate" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-form-group">
                            <label>End Date</label>
                            <input type="date" name="endDate" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-full-width modal-form-group">
                            <label>Description</label>
                            <textarea name="description" rows="2" placeholder="Project description" onChange={handleQuickAddChange}></textarea>
                        </div>
                    </div>
                )}

                {activeModal === 'employee' && (
                    <div className="modal-grid">
                        <div className="modal-form-group">
                            <label>First Name <span className="required-star">*</span></label>
                            <input name="firstName" placeholder="First Name" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-form-group">
                            <label>Last Name <span className="required-star">*</span></label>
                            <input name="lastName" placeholder="Last Name" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-form-group">
                            <label>Contact Number <span className="required-star">*</span></label>
                            <input name="contactNumber" placeholder="Contact Number" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-form-group">
                            <label>Email ID</label>
                            <input name="email" placeholder="Email" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-form-group">
                            <label>Gender <span className="required-star">*</span></label>
                            <select name="gender" onChange={handleQuickAddChange}>
                                <option value="">Select Gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                            </select>
                        </div>
                        <div className="modal-form-group">
                            <label>Role <span className="required-star">*</span></label>
                            <select name="roleId" onChange={handleQuickAddChange}>
                                <option value="">Select Role</option>
                                {modalOptions.roles.map(r => (
                                    <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="modal-full-width modal-form-group">
                            <label>Username <span className="required-star">*</span></label>
                            <input name="userName" placeholder="Username" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-form-group">
                            <label>Password <span className="required-star">*</span></label>
                            <input type="password" name="password" placeholder="Password" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-form-group">
                            <label>Confirm Password <span className="required-star">*</span></label>
                            <input type="password" name="confirmPassword" placeholder="Confirm Password" onChange={handleQuickAddChange} />
                        </div>
                    </div>
                )}

                {activeModal === 'product' && (
                    <div className="modal-grid">
                        <div className="modal-full-width modal-form-group">
                            <label>Product Name <span className="required-star">*</span></label>
                            <input name="productName" placeholder="Enter product name" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-form-group">
                            <label>Category <span className="required-star">*</span></label>
                            <Select
                                options={modalOptions.categories}
                                onChange={(sel) => handleQuickAddSelectChange(sel, 'productCategoryId')}
                                placeholder="Select Category"
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isClearable
                            />
                        </div>
                        <div className="modal-form-group">
                            <label>Unit</label>
                            <PaginatedSelect
                                apiUrl={GET_ALL_UNITS_API}
                                mapResponse={(u) => ({ value: u.id || u._id, label: u.unitName })}
                                value={quickAddFormData.unitId ? { value: quickAddFormData.unitId, label: quickAddFormData.unitName } : null}
                                onChange={(sel) => {
                                    handleQuickAddSelectChange(sel, 'unitId');
                                    setQuickAddFormData(prev => ({ ...prev, unitName: sel?.label || '' }));
                                }}
                                placeholder="Select Unit"
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isClearable
                            />
                        </div>
                        {/* <div className="modal-form-group">
                            <label>Size</label>
                            <input name="size" placeholder="Size (e.g. 10x10)" onChange={handleQuickAddChange} />
                        </div> */}
                        <div className="modal-full-width modal-form-group">
                            <label>Rate (₹) <span className="required-star">*</span></label>
                            <input type="number" name="defaultRate" placeholder="0.00" onChange={handleQuickAddChange} />
                        </div>
                        <div className="modal-full-width modal-form-group">
                            <label>Description</label>
                            <textarea name="description" rows="2" placeholder="Product description" onChange={handleQuickAddChange}></textarea>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Item Modal */}
            <Modal
                isOpen={itemModalOpen}
                onClose={() => setItemModalOpen(false)}
                title={isEditingItem ? 'Edit Item' : 'Add Item'}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button className="cancel-btn" onClick={() => setItemModalOpen(false)}>Cancel</button>
                        <button className="save-btn" onClick={handleSaveItem}>{isEditingItem ? 'Update Item' : 'Add to List'}</button>
                    </div>
                }
            >
                <div className="modal-grid">
                    <div className="modal-form-group full-width">
                        <label>Product Name</label>
                        <input
                            value={itemModalData.productName || ''}
                            readOnly
                            style={{ background: '#f1f5f9', color: '#64748b' }}
                        />
                    </div>

                    <div className="modal-form-group full-width">
                        <label>Description</label>
                        <textarea
                            rows="2"
                            value={itemModalData.productDescription || ''}
                            onChange={(e) => handleItemModalChange('productDescription', e.target.value)}
                            placeholder="Product Description"
                        ></textarea>
                    </div>

                    <div className="modal-form-group">
                        <label>Unit</label>
                        <PaginatedSelect
                            apiUrl={GET_ALL_UNITS_API}
                            mapResponse={(u) => ({ value: u.id || u._id, label: u.unitName })}
                            value={itemModalData.unit}
                            onChange={(sel) => handleItemModalChange('unit', sel)}
                            placeholder="Select Unit"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            isClearable
                            menuPosition="fixed"
                        />
                    </div>

                    <div className="modal-form-group">
                        <label>Size</label>
                        <input
                            value={itemModalData.size || ''}
                            onChange={(e) => handleItemModalChange('size', e.target.value)}
                            placeholder="e.g. 10x10"
                        />
                    </div>

                    <div className="modal-form-group">
                        <label>Quantity</label>
                        <input
                            type="number"
                            min="1"
                            value={itemModalData.qty}
                            onChange={(e) => handleItemModalChange('qty', e.target.value)}
                        />
                    </div>

                    <div className="modal-form-group">
                        <label>Rate</label>
                        <input
                            type="number"
                            min="0"
                            value={itemModalData.rate}
                            onChange={(e) => handleItemModalChange('rate', e.target.value)}
                        />
                    </div>

                    <div className="modal-form-group full-width">
                        <label>Total</label>
                        <input
                            type="number"
                            value={itemModalData.total}
                            readOnly
                            style={{ background: '#f1f5f9' }}
                        />
                    </div>
                </div>
            </Modal>

            {/* WhatsApp Notification Loader Modal */}
            {whatsappLoading && (
                <div className="whatsapp-loader-overlay">
                    <div className="whatsapp-loader-card">
                        <div className="whatsapp-icon-wrapper">
                            <FaWhatsapp className="whatsapp-anim-icon" size={50} />
                            <div className="pulse-ring"></div>
                        </div>
                        <h3>Sending Status</h3>
                        <p>Notifying vendors via WhatsApp...</p>
                        <div className="wa-progress-container">
                            <div className="wa-progress-bar">
                                <div
                                    className="wa-progress-fill"
                                    style={{ width: `${(whatsappProgress.current / whatsappProgress.total) * 100}%` }}
                                ></div>
                            </div>
                            <div className="wa-progress-stats">
                                <span>{whatsappProgress.vendorName || 'Initializing...'}</span>
                                <span>{whatsappProgress.current} / {whatsappProgress.total}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddGenerateRequest;
